import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  HelpBanner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@betstarters/ui'
import { Ban, Pencil, Plus, RotateCcw } from 'lucide-react'
import { api } from '../api'
import { useCapabilities } from '../hooks/useCapabilities'
import { useReference } from '../hooks/useReference'
import { messageOf } from '../lib/apiError'
import {
  CatalogToolbar,
  ErrorBanner,
  PageHeading,
  Pagination,
  StatusBadge,
} from '../components/catalog'
import { Combobox } from '../components/controls'
import { Flag } from '../components/flag'
import { PrefixForm, emptyPrefixForm, type PrefixFormValue } from '../components/geoForms'

export interface Prefix {
  prefixId: number
  prefix: string
  countryId: number | null
  countryName: string
  isoCode2: string
  linkedCountry: string
  isDisabledByPlatform: boolean
  creationDate: string
  lastModificationDate: string | null
}

interface PrefixPage {
  prefixes: Prefix[]
  total: number
  offset: number
  limit: number
}

const PAGE_SIZE = 50

/**
 * PrefixesPage is the international dialling prefix catalogue —
 * [Catalog].[Geographic.Prefix] in the BsConnekto database.
 *
 * Two things the table does that the UI has to respect: the id is chosen by the
 * caller (no IDENTITY), and the row's country label is its own — production
 * keeps "DRC" next to a country row reading "CONGO, THE DEMOCRATIC REPUBLIC OF
 * THE". The list shows both, so a difference reads as intent rather than rot.
 */
export function PrefixesPage() {
  const { t } = useTranslation('app')
  const qc = useQueryClient()
  const { has } = useCapabilities()
  const canWrite = has('catalog:write')
  const { countries } = useReference()

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [countryFilter, setCountryFilter] = useState('')
  const [includeDisabled, setIncludeDisabled] = useState(true)
  const [offset, setOffset] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Prefix | null>(null)
  const [form, setForm] = useState<PrefixFormValue>(emptyPrefixForm)
  const [error, setError] = useState('')

  const params = {
    search,
    includeDisabled: String(includeDisabled),
    offset,
    limit: PAGE_SIZE,
    ...(countryFilter ? { countryId: countryFilter } : {}),
  }

  const { data, isLoading } = useQuery({
    queryKey: ['prefixes', params],
    queryFn: async (): Promise<PrefixPage> => {
      const res = await api.get('/prefixes', { params })
      return res.data as PrefixPage
    },
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['prefixes'] })

  const openCreate = () => {
    setEditing(null)
    setForm(emptyPrefixForm)
    setError('')
    setDialogOpen(true)
  }

  const openEdit = (p: Prefix) => {
    setEditing(p)
    setForm({
      prefixId: String(p.prefixId),
      prefix: p.prefix,
      countryId: p.countryId === null ? '' : String(p.countryId),
      countryName: p.countryName,
      isoCode2: p.isoCode2,
    })
    setError('')
    setDialogOpen(true)
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        prefix: form.prefix.trim(),
        countryId: form.countryId === '' ? null : Number(form.countryId),
        countryName: form.countryName.trim(),
        isoCode2: form.isoCode2.trim(),
        isDisabledByPlatform: editing?.isDisabledByPlatform ?? false,
      }
      return editing
        ? api.put(`/prefixes/${editing.prefixId}`, body)
        : api.post('/prefixes', { ...body, prefixId: Number(form.prefixId) })
    },
    onSuccess: () => {
      setDialogOpen(false)
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('prefixes.errors.save'))),
  })

  const toggle = useMutation({
    mutationFn: (p: Prefix) =>
      p.isDisabledByPlatform
        ? api.post(`/prefixes/${p.prefixId}/restore`)
        : api.delete(`/prefixes/${p.prefixId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('prefixes.errors.save'))),
  })

  const prefixes = data?.prefixes ?? []
  const total = data?.total ?? 0

  return (
    <div>
      <PageHeading
        title={t('prefixes.title')}
        subtitle={t('prefixes.subtitle')}
        action={
          canWrite && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger
                render={
                  <Button onClick={openCreate}>
                    <Plus />
                    {t('prefixes.newPrefix')}
                  </Button>
                }
              />
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>
                    {editing ? t('prefixes.editPrefix') : t('prefixes.newPrefix')}
                  </DialogTitle>
                </DialogHeader>
                <PrefixForm
                  value={form}
                  onChange={setForm}
                  onSubmit={() => save.mutate()}
                  onCancel={() => setDialogOpen(false)}
                  countries={countries}
                  isCreate={editing === null}
                  pending={save.isPending}
                  error={error}
                  submitLabel={editing ? t('common.save') : t('common.create')}
                />
              </DialogContent>
            </Dialog>
          )
        }
      />

      <HelpBanner storageKey="prefixes">
        <p>{t('prefixes.help.line1')}</p>
        <p>{t('prefixes.help.line2')}</p>
      </HelpBanner>

      <CatalogToolbar
        searchInput={searchInput}
        onSearchInput={setSearchInput}
        onSubmitSearch={() => {
          setSearch(searchInput)
          setOffset(0)
        }}
        searchPlaceholder={t('prefixes.searchPlaceholder')}
        includeDisabled={includeDisabled}
        onToggleDisabled={() => {
          setIncludeDisabled(!includeDisabled)
          setOffset(0)
        }}
        filtered={search !== '' || !includeDisabled || countryFilter !== ''}
        onClear={() => {
          setSearch('')
          setSearchInput('')
          setCountryFilter('')
          setIncludeDisabled(true)
          setOffset(0)
        }}
      >
        <Combobox
          value={countryFilter}
          onValueChange={(v) => {
            setCountryFilter(v)
            setOffset(0)
          }}
          items={countries.map((c) => ({ value: String(c.id), label: `${c.isoCode2} — ${c.name}` }))}
          placeholder={t('prefixes.allCountries')}
          className="w-56"
        />
      </CatalogToolbar>

      {!dialogOpen && <ErrorBanner message={error} />}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-16">{t('prefixes.columns.id')}</TableHead>
                  <TableHead className="w-24">{t('prefixes.columns.prefix')}</TableHead>
                  <TableHead>{t('prefixes.columns.countryName')}</TableHead>
                  <TableHead className="w-12" aria-label={t('countries.columns.flag')} />
                  <TableHead className="w-20">{t('prefixes.columns.iso2')}</TableHead>
                  <TableHead className="w-64">{t('prefixes.columns.linkedCountry')}</TableHead>
                  <TableHead className="w-32">{t('common.columns.status')}</TableHead>
                  {canWrite && (
                    <TableHead className="w-28 text-right">{t('common.columns.actions')}</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {prefixes.map((p) => (
                  <TableRow key={p.prefixId}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {p.prefixId}
                    </TableCell>
                    <TableCell className="font-mono font-medium">+{p.prefix}</TableCell>
                    <TableCell>{p.countryName}</TableCell>
                    <TableCell>
                      <Flag region={p.isoCode2} />
                    </TableCell>
                    <TableCell className="font-mono text-xs">{p.isoCode2}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {p.countryId === null ? (
                        <Badge variant="outline">{t('prefixes.noCountry')}</Badge>
                      ) : (
                        p.linkedCountry || `#${p.countryId}`
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge disabled={p.isDisabledByPlatform} />
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={t('common.edit')}
                            onClick={() => openEdit(p)}
                          >
                            <Pencil />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={p.isDisabledByPlatform ? t('common.restore') : t('common.retire')}
                            disabled={toggle.isPending}
                            onClick={() => toggle.mutate(p)}
                          >
                            {p.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {prefixes.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={canWrite ? 7 : 6}
                      className="py-8 text-center text-muted-foreground"
                    >
                      {t('prefixes.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <Pagination
            total={total}
            offset={offset}
            pageSize={PAGE_SIZE}
            onOffset={setOffset}
            label={t('prefixes.total', { count: total })}
          />
        </>
      )}
    </div>
  )
}
